import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import { BarChart, LineChart } from "@mui/x-charts";
import { AdminAPI } from "../api/admin";
import StatCard from "../components/StatCard";
import { useNavigate } from 'react-router-dom';
import { MetricTile, PageHeading, PortalPage, number, money, portalColors } from '../components/SuperAdminUI';

function formatCurrency(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function formatDate(value) {
  if (!value) return "N/A";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "N/A"
    : parsed.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

export default function BillingRevenue() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revenueData, setRevenueData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txnPage, setTxnPage] = useState(1);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnFilters, setTxnFilters] = useState({ status: "", startDate: "", endDate: "" });
  const [partnerInvoices, setPartnerInvoices] = useState([]);
  const [partnerTotal, setPartnerTotal] = useState(0);
  const [partnerSummary, setPartnerSummary] = useState({});
  const [partnerPage, setPartnerPage] = useState(1);
  const [partnerStatus, setPartnerStatus] = useState('');
  const [partnerSearch, setPartnerSearch] = useState('');

  const loadOverview = async () => {
    const [revenueRes, recentTxnRes] = await Promise.all([
      AdminAPI.getRevenueDashboard(),
      AdminAPI.getAllTransactions({ page: 1, limit: 5 }),
    ]);
    setRevenueData(revenueRes?.data?.data || null);
    setTransactions(recentTxnRes?.data?.data?.transactions || []);
  };

  const loadTransactions = async () => {
    const res = await AdminAPI.getAllTransactions({
      page: txnPage,
      limit: 20,
      ...txnFilters,
    });
    setTransactions(res?.data?.data?.transactions || []);
    setTxnTotal(res?.data?.data?.pagination?.total || 0);
  };

  const loadPartnerInvoices = async () => {
    const res = await AdminAPI.getAllPartnerInvoices({ page: partnerPage, limit: 20, status: partnerStatus, search: partnerSearch });
    setPartnerInvoices(res?.data?.data?.rows || []);
    setPartnerTotal(Number(res?.data?.data?.total || 0));
    setPartnerSummary(res?.data?.data?.summary || {});
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      if (activeTab === 0) {
        await loadOverview();
      } else if (activeTab === 1) {
        await loadTransactions();
      } else {
        await loadPartnerInvoices();
      }
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, txnPage, partnerPage, partnerStatus]);

  const dailyRevenueSeries = useMemo(
    () =>
      (revenueData?.dailyRevenue || []).map((row) => ({
        date: new Date(row.date).toLocaleDateString("en-IN", { month: "short", day: "numeric" }),
        revenue: Number(row.revenue || 0),
      })),
    [revenueData?.dailyRevenue]
  );

  const planRevenueSeries = useMemo(
    () =>
      (revenueData?.planRevenue || []).map((row) => ({
        plan: row.plan || "Unknown",
        revenue: Number(row.revenue || 0),
      })),
    [revenueData?.planRevenue]
  );

  const totalStats = revenueData?.totalStats || {};
  const monthlyComparison = revenueData?.monthlyComparison || {};

  return (
    <PortalPage>
      <PageHeading title="Billing & collections" description="Enterprise payments and Susha invoices to partners, kept in separate ledgers." actions={<Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>} />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 3, borderColor: "#dbe6f3", borderRadius: 2 }}>
        <Tabs value={activeTab} onChange={(_, next) => setActiveTab(next)} sx={{ px: 1 }}>
          <Tab label="Enterprise collections" />
          <Tab label="Enterprise transactions" />
          <Tab label="Partner invoices" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>These amounts come from completed enterprise payment transactions. Partner invoices are shown in their own tab. Payment totals are not recurring revenue.</Alert>
              <Grid container spacing={2.5} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    label="Enterprise collections"
                    value={formatCurrency(totalStats.totalRevenue)}
                    icon={<AccountBalanceIcon color="primary" fontSize="large" />}
                    caption={`${Number(totalStats.totalTransactions || 0)} completed transactions`}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    label="Base price total"
                    value={formatCurrency(totalStats.grossRevenue)}
                    icon={<TrendingUpIcon color="success" fontSize="large" />}
                    caption="Before credits and deductions"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    label="Completed Transactions"
                    value={Number(totalStats.totalTransactions || 0)}
                    icon={<ReceiptIcon color="primary" fontSize="large" />}
                    caption="Successful payments processed"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    label="Avg Transaction Value"
                    value={formatCurrency(totalStats.avgTransactionValue)}
                    icon={<AssessmentIcon color="primary" fontSize="large" />}
                    caption={`This month: ${formatCurrency(monthlyComparison.thisMonth)}`}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2.5} sx={{ mb: 4 }}>
                <Grid item xs={12} md={7}>
                  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, borderColor: "#dbe6f3", bgcolor: "#fff" }}>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 800 }}>
                      Daily enterprise collections
                    </Typography>
                    <Typography variant="body2" color="text.secondary" display="block" mb={2}>
                      Completed payments grouped by transaction creation date over the last 30 days.
                    </Typography>
                    {dailyRevenueSeries.length > 0 ? (
                      <Box sx={{ width: "100%", overflow: "auto" }}>
                        <LineChart
                          xAxis={[{ scaleType: "point", data: dailyRevenueSeries.map((d) => d.date) }]}
                          series={[{ data: dailyRevenueSeries.map((d) => d.revenue), label: "Revenue", area: true, color: "#054693" }]}
                          height={300}
                          margin={{ top: 20, bottom: 40, left: 70, right: 20 }}
                          sx={{ minWidth: { xs: 420, sm: "100%" } }}
                        />
                      </Box>
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <Typography variant="body2" color="text.secondary">
                          No revenue data available
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, borderColor: "#dbe6f3", bgcolor: "#fff" }}>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 800 }}>
                      Enterprise collections by plan
                    </Typography>
                    <Typography variant="body2" color="text.secondary" display="block" mb={2}>
                      Which subscription plans drive the most billing.
                    </Typography>
                    {planRevenueSeries.length > 0 ? (
                      <Box sx={{ width: "100%", overflow: "auto" }}>
                        <BarChart
                          xAxis={[{ scaleType: "band", data: planRevenueSeries.map((d) => d.plan) }]}
                          series={[{ data: planRevenueSeries.map((d) => d.revenue), label: "Revenue", color: "#0f9d58" }]}
                          height={300}
                          margin={{ top: 20, bottom: 80, left: 60, right: 20 }}
                          sx={{ minWidth: { xs: 420, sm: "100%" } }}
                        />
                      </Box>
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <Typography variant="body2" color="text.secondary">
                          No plan data available
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, borderColor: "#dbe6f3", bgcolor: "#fff" }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 800 }}>
                  Recent Transactions
                </Typography>
                <TableContainer sx={{ overflow: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Enterprise</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(transactions || []).length > 0 ? (
                        transactions.map((txn) => (
                          <TableRow key={txn.id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {txn.enterpriseName || "N/A"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {txn.enterpriseEmail || ""}
                              </Typography>
                            </TableCell>
                            <TableCell>{txn.plan || "N/A"}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {formatCurrency(txn.totalAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={txn.status || "unknown"}
                                color={txn.status === "completed" ? "success" : txn.status === "pending" ? "warning" : "default"}
                              />
                            </TableCell>
                            <TableCell>{formatDate(txn.createdAt)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No transactions found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}

          {activeTab === 1 && (
            <>
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2, borderColor: "#dbe6f3" }} variant="outlined">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={txnFilters.status}
                        label="Status"
                        onChange={(e) => setTxnFilters((prev) => ({ ...prev, status: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Start Date"
                      value={txnFilters.startDate}
                      onChange={(e) => setTxnFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="End Date"
                      value={txnFilters.endDate}
                      onChange={(e) => setTxnFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={3}>
                    <Button fullWidth variant="contained" onClick={() => loadTransactions()}>
                      Apply Filters
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, borderColor: "#dbe6f3", bgcolor: "#fff" }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 800 }}>
                  Transactions
                </Typography>
                <TableContainer sx={{ overflow: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Enterprise</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Billing</TableCell>
                        <TableCell align="right">Base Price</TableCell>
                        <TableCell align="right">Credits</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(transactions || []).length > 0 ? (
                        transactions.map((txn) => (
                          <TableRow key={txn.id} hover>
                            <TableCell sx={{ fontWeight: 700 }}>{txn.enterpriseName || "N/A"}</TableCell>
                            <TableCell>{txn.enterpriseEmail || "N/A"}</TableCell>
                            <TableCell>{txn.plan || "N/A"}</TableCell>
                            <TableCell>{txn.billingCycle || "N/A"}</TableCell>
                            <TableCell align="right">{formatCurrency(txn.basePrice)}</TableCell>
                            <TableCell align="right">{txn.creditsApplied > 0 ? `-₹${Number(txn.creditsApplied).toLocaleString("en-IN")}` : "-"}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatCurrency(txn.totalAmount)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={txn.status || "unknown"}
                                color={txn.status === "completed" ? "success" : txn.status === "pending" ? "warning" : "error"}
                              />
                            </TableCell>
                            <TableCell>{formatDate(txn.createdAt)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No transactions found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {txnTotal > 20 && (
                  <Box display="flex" justifyContent="center" p={2}>
                    <Button onClick={() => setTxnPage((p) => Math.max(1, p - 1))} disabled={txnPage === 1}>
                      Previous
                    </Button>
                    <Typography sx={{ mx: 2, alignSelf: "center" }}>
                      Page {txnPage} of {Math.ceil(txnTotal / 20)}
                    </Typography>
                    <Button onClick={() => setTxnPage((p) => p + 1)} disabled={txnPage >= Math.ceil(txnTotal / 20)}>
                      Next
                    </Button>
                  </Box>
                )}
              </Paper>
            </>
          )}
          {activeTab === 2 && <>
            <Alert severity="info" sx={{ mb: 2 }}>These are Susha invoices issued to partners. A sent invoice is not a recorded payment.</Alert>
            <Grid container spacing={1.7} sx={{ mb: 2 }}><Grid size={{ xs: 6, lg: 3 }}><MetricTile label="All partner invoices" value={number(partnerSummary.total)} detail="All statuses" /></Grid><Grid size={{ xs: 6, lg: 3 }}><MetricTile label="Sent" value={number(partnerSummary.sent)} detail="Not confirmed collected" accent={portalColors.teal} /></Grid><Grid size={{ xs: 6, lg: 3 }}><MetricTile label="Overdue sent" value={number(partnerSummary.overdue)} detail="Past due date" accent={portalColors.red} /></Grid><Grid size={{ xs: 6, lg: 3 }}><MetricTile label="Sent face value · INR" value={money(partnerSummary.sent_inr)} detail="Invoice amount, not cash" accent={portalColors.amber} /></Grid></Grid>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}><TextField size="small" placeholder="Search partner or invoice" value={partnerSearch} onChange={(event) => setPartnerSearch(event.target.value)} sx={{ flex: 1 }} /><TextField select size="small" label="Status" value={partnerStatus} onChange={(event) => { setPartnerStatus(event.target.value); setPartnerPage(1); }} sx={{ minWidth: 155 }}><MenuItem value="">All statuses</MenuItem><MenuItem value="draft">Draft</MenuItem><MenuItem value="sent">Sent</MenuItem><MenuItem value="paid">Paid</MenuItem><MenuItem value="void">Void</MenuItem></TextField><Button variant="outlined" onClick={() => { setPartnerPage(1); void loadPartnerInvoices(); }}>Search</Button></Stack></Paper>
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}><Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e3eaf3' }}><Typography fontWeight={800}>Partner invoices</Typography><Typography variant="caption" color="text.secondary">Create a new partner invoice from the partner's detail panel.</Typography></Box><TableContainer><Table size="small" sx={{ minWidth: 850 }}><TableHead><TableRow><TableCell>Invoice</TableCell><TableCell>Partner</TableCell><TableCell>Period</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell>Due</TableCell><TableCell /></TableRow></TableHead><TableBody>{partnerInvoices.map((invoice) => <TableRow key={invoice.id} hover><TableCell sx={{ fontWeight: 750 }}>{invoice.invoice_number}</TableCell><TableCell><Typography fontWeight={700} fontSize={13}>{invoice.partner_name}</Typography><Typography variant="caption" color="text.secondary">{invoice.partner_email}</Typography></TableCell><TableCell>{invoice.billing_period_label}</TableCell><TableCell align="right">{invoice.currency} {Number(invoice.amount || 0).toLocaleString('en-IN')}</TableCell><TableCell><Chip size="small" label={invoice.status} color={invoice.status === 'paid' ? 'success' : invoice.status === 'sent' ? 'info' : 'default'} /></TableCell><TableCell>{invoice.due_date || '—'}</TableCell><TableCell><Button size="small" onClick={() => navigate(`/superadmin/partners?partner=${invoice.partner_id}`)}>Partner</Button></TableCell></TableRow>)}{!partnerInvoices.length && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>No partner invoices match this filter.</TableCell></TableRow>}</TableBody></Table></TableContainer><Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ p: 1.5 }}><Button size="small" disabled={partnerPage <= 1} onClick={() => setPartnerPage((value) => value - 1)}>Previous</Button><Typography variant="caption">Page {partnerPage} of {Math.max(1, Math.ceil(partnerTotal / 20))}</Typography><Button size="small" disabled={partnerPage >= Math.ceil(partnerTotal / 20)} onClick={() => setPartnerPage((value) => value + 1)}>Next</Button></Stack></Paper>
          </>}
        </>
      )}
    </PortalPage>
  );
}
